import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Download, Search, Building2, Clock, CheckCircle, XCircle, RefreshCw, Eye, FileSpreadsheet, Users } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CorporateReports = () => {
  const { token } = useAdmin();
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await axios.get(`${API}/admin/reports/corporate-enquiries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEnquiries(response.data.enquiries || []);
      setStats({
        total: response.data.total || 0,
        byStatus: response.data.by_status || {},
        byEmployeeSize: response.data.by_employee_size || {},
        byGst: response.data.by_gst || {}
      });
    } catch (error) {
      toast.error('Failed to fetch corporate enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchEnquiries();
    }
  }, [token, statusFilter]);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const response = await axios.get(`${API}/admin/reports/corporate-enquiries/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corporate_enquiries_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Corporate enquiries exported successfully');
    } catch (error) {
      toast.error('Failed to download corporate enquiries');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedEnquiry || !newStatus) return;
    
    try {
      await axios.put(
        `${API}/admin/reports/corporate-enquiries/${selectedEnquiry.id}/status?status=${newStatus}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Enquiry status updated');
      setIsDialogOpen(false);
      setSelectedEnquiry(null);
      setNewStatus('');
      setNotes('');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openStatusDialog = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setNewStatus(enquiry.status);
    setNotes(enquiry.notes || '');
    setIsDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      proposal_sent: 'bg-purple-100 text-purple-800',
      negotiating: 'bg-orange-100 text-orange-800',
      converted: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredEnquiries = enquiries.filter(enq => {
    const matchesSearch = 
      enq.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enq.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enq.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enq.phone?.includes(searchTerm);
    return matchesSearch;
  });

  return (
    <div className="space-y-6" data-testid="corporate-reports-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Corporate Enquiries</h1>
          <p className="text-gray-600 mt-1">Manage corporate proposal requests</p>
        </div>
        <Button 
          onClick={handleDownload} 
          disabled={isDownloading}
          className="bg-green-600 hover:bg-green-700"
          data-testid="download-corporate-btn"
        >
          {isDownloading ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileSpreadsheet className="w-4 h-4 mr-2" />
          )}
          Download Excel
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Enquiries</p>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-new">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">New Enquiries</p>
                <p className="text-2xl font-bold">{stats.byStatus?.new || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-converted">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Converted</p>
                <p className="text-2xl font-bold">{stats.byStatus?.converted || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-gst">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">GST Registered</p>
                <p className="text-2xl font-bold">{stats.byGst?.yes || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by company, contact person, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-corporate"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                <SelectItem value="negotiating">Negotiating</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchEnquiries} data-testid="refresh-corporate">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Enquiries Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filteredEnquiries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No corporate enquiries found. Enquiries from the Corporate Proposal form will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact Person</TableHead>
                    <TableHead>Employee Size</TableHead>
                    <TableHead>Monthly Volume</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEnquiries.map((enq) => (
                    <TableRow key={enq.id} data-testid={`corporate-row-${enq.id}`}>
                      <TableCell className="font-medium">{enq.company_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{enq.contact_person}</div>
                          <div className="text-gray-500">{enq.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>{enq.employee_size || '-'}</TableCell>
                      <TableCell>{enq.monthly_travel_volume || '-'}</TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(enq.status)}>
                          {enq.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {enq.created_at ? format(new Date(enq.created_at), 'dd MMM yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStatusDialog(enq)}
                          data-testid={`view-corporate-${enq.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enquiry Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Corporate Enquiry Details</DialogTitle>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-gray-500">Company Name</Label>
                  <p className="font-medium">{selectedEnquiry.company_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Contact Person</Label>
                  <p className="font-medium">{selectedEnquiry.contact_person}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p className="font-medium">{selectedEnquiry.email}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Phone</Label>
                  <p className="font-medium">{selectedEnquiry.phone}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Employee Size</Label>
                  <p className="font-medium">{selectedEnquiry.employee_size || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Monthly Volume</Label>
                  <p className="font-medium">{selectedEnquiry.monthly_travel_volume || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Travel Services</Label>
                  <p className="font-medium">{selectedEnquiry.major_travel_services || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">GST Registered</Label>
                  <p className="font-medium">{selectedEnquiry.gst_registered || '-'}</p>
                </div>
              </div>
              
              {selectedEnquiry.requirements && (
                <div>
                  <Label className="text-gray-500">Requirements</Label>
                  <p className="text-sm mt-1 p-3 bg-gray-50 rounded-lg">{selectedEnquiry.requirements}</p>
                </div>
              )}

              <div className="border-t pt-4">
                <Label>Update Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="mt-2" data-testid="update-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                    <SelectItem value="negotiating">Negotiating</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this enquiry..."
                  className="mt-2"
                  data-testid="corporate-notes"
                />
              </div>

              <Button 
                onClick={handleStatusUpdate} 
                className="w-full"
                data-testid="save-corporate-status"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CorporateReports;
